import { css } from '@emotion/react';
import { Stack } from '@spark-web/stack';
import { ThemeProvider } from '@spark-web/theme';
import { useQueryState } from 'nuqs';
import { LoaderFunctionArgs, Outlet, useLoaderData } from 'react-router';

import { DatadogRum } from '~/components/datadog-rum';
import { PremarketplaceDialog } from '~/components/premarketplace/premarketplace-dialog';
import { usePartnerConfig } from '~/context/PartnerConfigContext';
import { PremarketplaceProvider } from '~/provider/PremarketplaceProvider';
import { StepProvider } from '~/provider/StepProvider';
import { getCategories } from '~/services/categories';
import { fetchEnterprisePartnerByChannel } from '~/services/enterprisePartner';
import { parseAsMarketplaceChannel, parseChannel } from '~/utils/channels';
import { toSparkTheme } from '~/utils/theme';

export const loader = async ({ request }: LoaderFunctionArgs) => {
  const currentUrl = new URL(request.url);
  const channelParam = currentUrl.searchParams.get('channel');

  const channel = parseChannel(channelParam) ?? 'brighte';

  const partnerResult = await fetchEnterprisePartnerByChannel(channel);

  // fetchEnterprisePartnerByChannel distinguishes:
  //   undefined — lookup failed (API error / timeout). Treat as transient: 502 so retries don't get cached as 404.
  //   null      — channel doesn't resolve to a partner. Permanent: 404.
  // (503 is reserved by the root ErrorBoundary for the maintenance-page payload — using 502 keeps
  //  "upstream API unreachable" distinct from "marketplace is in scheduled maintenance".)
  if (partnerResult === undefined) {
    throw new Response(null, { status: 502 });
  }
  if (partnerResult === null) {
    throw new Response(null, { status: 404 });
  }

  const channelConfig = partnerResult.configuration?.channels?.find((c) => c.slug === channel);
  // Authorization gate: a channel is allowed to reach pre-marketplace only when the enterprise
  // API has populated `preMarketplace` on it. Unauthorized channels (e.g. act*, brighte, electrify-2515)
  // expose all other channel metadata but `preMarketplace` is null — reject with 404 here.
  if (!channelConfig?.preMarketplace) {
    throw new Response(null, { status: 404 });
  }

  const partner = partnerResult.id;
  const channelFinancialProductId = channelConfig.financialProductId ?? undefined;
  const categories = await getCategories(channelFinancialProductId, partner);

  const subCategories = categories.flatMap((category) => category.subCategories).filter((sc) => !!sc);

  return { categories: subCategories, partner };
};

function PremarketplaceParentLayout() {
  const { categories, partner } = useLoaderData<typeof loader>();

  const [channel] = useQueryState('channel', parseAsMarketplaceChannel.withDefault('brighte'));
  const config = usePartnerConfig();

  const theme = toSparkTheme(config?.configuration?.sparkThemeName);

  return (
    <ThemeProvider theme={theme}>
      <Stack height="full" css={css({ minHeight: '100vh' })}>
        <Stack as="main" paddingTop="small" tabIndex={-1} flex={1}>
          <PremarketplaceProvider channel={channel} categories={categories}>
            <DatadogRum />

            <StepProvider partner={partner ?? undefined}>
              <PremarketplaceDialog>
                <Outlet context={{ channel, partner: partner ?? undefined }} />
              </PremarketplaceDialog>
            </StepProvider>
          </PremarketplaceProvider>
        </Stack>
      </Stack>
    </ThemeProvider>
  );
}

export default PremarketplaceParentLayout;
